import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { useGroups, useInviteUser, useOrganisationUsers, useRoles, useUserLifecycle } from "../api/hooks";
import { useAuth } from "../auth/AuthProvider";
import { useStepUp } from "../auth/StepUpProvider";
import { useEnvironmentName } from "../components/Environment";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../components/ui";
import { RolePills } from "./access/parts";

function canManageFrom(me: ReturnType<typeof useAuth>["me"]): boolean {
  return !me?.viewAs || me.viewAs.mode === "write";
}

/**
 * ADR-0013: a tenant's managers manage its people. An invitation creates a
 * person with no access; roles are granted per environment on its Access
 * tab, by whoever manages it. Gairm's part is tenants, not users.
 */
export function UsersPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { me } = useAuth();
  const q = useOrganisationUsers();
  const invite = useInviteUser();
  const lifecycle = useUserLifecycle();
  // ADR-0017 section 6: every role a person holds per environment, and the groups they are in.
  const roles = useRoles();
  const groups = useGroups();
  const envName = useEnvironmentName();
  const groupsOf = (userId: string) => (groups.data?.groups ?? []).filter((g) => g.members.some((m) => m.userId === userId));
  const { ensureFresh } = useStepUp();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<unknown>();
  const id = useId();
  const guarded = (run: () => void) => void ensureFresh().then(run, () => undefined);
  // #591: Gairm support is the backstop for sign-in help, so staff answer it from the customer's portal with editing on.
  const canRepair = canManageFrom(me);
  const [repaired, setRepaired] = useState<string>();

  if (q.isPending) return <LoadingState label="Loading your organization's people" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { users, canManage } = q.data;
  const current = users.filter((u) => !u.removedAt);
  const removed = users.filter((u) => u.removedAt);

  return (
    <>
      {embedded ? (
        <h2 className="text-xl font-semibold">Your organization's people</h2>
      ) : (
        <PageHeader title="Users" subtitle="Who can sign in to your organization's portal. Access to each environment is granted on that environment's Access page." />
      )}
      {canManage && !me?.viewAs && (
        <Card title="Invite someone">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(undefined);
              guarded(() =>
                invite.mutate(
                  { email },
                  { onSuccess: () => setEmail(""), onError: setError },
                ),
              );
            }}
          >
            <label htmlFor={id} className="text-sm">
              <span className="block font-medium">Email</span>
              <input id={id} type="email" className={`${inputClass} w-80 max-w-full`} value={email} required onChange={(e) => setEmail(e.target.value)} placeholder="name@yourcompany.example" />
              <span className="mt-1 block text-xs text-neutral-500">They get a temporary password by email and set up an authenticator on first sign-in. They can see nothing until someone grants them a role.</span>
            </label>
            <button type="submit" className={btnPrimary} disabled={invite.isPending || !email}>
              {invite.isPending ? "Inviting…" : "Invite"}
            </button>
          </form>
          {invite.isSuccess && <p role="status" className="mt-2 text-sm text-green-700 dark:text-green-300">Invited {invite.data.email}.</p>}
          {error !== undefined && <ErrorState error={error} lead="Couldn’t do that." />}
        </Card>
      )}
      <div className="mt-6">
        <DataTable
          caption="People in your organization"
          rows={current}
          rowKey={(u) => u.userId}
          sortBy={(u) => u.email ?? u.userId}
          empty={<EmptyState title="Nobody yet" hint="Invite the first person above." />}
          columns={[
            {
              key: "who",
              header: "Person",
              render: (u) => (
                <div>
                  <div>{u.displayName ? <>{u.displayName} <span className="text-neutral-500">({u.email})</span></> : (u.email ?? u.userId)}</div>
                  {u.userId === me?.subject && <span className="text-xs text-neutral-500">you</span>}
                </div>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (u) => (
                <div className="space-y-1">
                  {u.disabledAt ? <StatusPill status="disabled" tone="warning" /> : !u.lastSeenAt ? <StatusPill status="invited" tone="neutral" /> : <StatusPill status="active" tone="ok" />}
                  {u.helpRequest && (
                    <div className="text-xs text-amber-800 dark:text-amber-300">
                      Asked for {u.helpRequest.kind === "authenticator" ? "an authenticator reset" : "a new invitation"} <RelativeTime iso={u.helpRequest.at} /> · {u.helpRequest.reference}
                    </div>
                  )}
                </div>
              ),
            },
            { key: "seen", header: "Last seen", render: (u) => (u.lastSeenAt ? <RelativeTime iso={u.lastSeenAt} /> : u.invitedAt ? <span className="text-sm text-neutral-500">never · invited <RelativeTime iso={u.invitedAt} />{u.invitedBy ? ` by ${u.invitedBy}` : ""}</span> : "—") },
            {
              key: "roles",
              header: "Access",
              render: (u) =>
                u.roles.length === 0 ? (
                  <span className="text-sm text-neutral-500">none</span>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {u.roles.map((r) => (
                      <li key={r.environmentId} className="flex flex-wrap items-center gap-2">
                        <Link to={`/access/${r.environmentId}`} className="underline">{envName(r.environmentId)}</Link>
                        <RolePills roleIds={r.roleIds} roles={roles.data?.roles} />
                      </li>
                    ))}
                  </ul>
                ),
            },
            {
              key: "groups",
              header: "Groups",
              render: (u) => {
                const mine = groupsOf(u.userId);
                return mine.length === 0 ? <span className="text-sm text-neutral-500">-</span> : <span className="text-sm">{mine.map((g) => g.name).join(", ")}</span>;
              },
            },
            {
              key: "act",
              header: "",
              render: (u) =>
                canManage && u.userId !== me?.subject && (canRepair || !me?.viewAs) ? (
                  <span className="flex flex-wrap gap-2">
                    {canRepair && !u.disabledAt && (u.lastSeenAt ? (
                      <button type="button" className={`${u.helpRequest?.kind === "authenticator" ? btnPrimary : btnSecondary} min-h-8 px-3 text-xs`} disabled={lifecycle.isPending} onClick={() => guarded(() => lifecycle.mutate({ userId: u.userId, action: "reset-authenticator" }, { onSuccess: () => setRepaired(`${u.email ?? u.userId}'s authenticator was reset and every session signed out. They set up a new one at their next sign-in.`), onError: setError }))}>
                        Reset authenticator
                      </button>
                    ) : (
                      <button type="button" className={`${u.helpRequest?.kind === "invitation" ? btnPrimary : btnSecondary} min-h-8 px-3 text-xs`} disabled={lifecycle.isPending} onClick={() => guarded(() => lifecycle.mutate({ userId: u.userId, action: "resend-invitation" }, { onSuccess: () => setRepaired(`A new invitation is on its way to ${u.email ?? u.userId}.`), onError: setError }))}>
                        Resend invitation
                      </button>
                    ))}
                    {!me?.viewAs && <>
                    <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={lifecycle.isPending} onClick={() => guarded(() => lifecycle.mutate({ userId: u.userId, action: u.disabledAt ? "enable" : "disable" }, { onError: setError }))}>
                      {u.disabledAt ? "Enable" : "Disable"}
                    </button>
                    <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={lifecycle.isPending} onClick={() => guarded(() => lifecycle.mutate({ userId: u.userId, action: "remove" }, { onError: setError }))}>
                      Remove
                    </button>
                    </>}
                  </span>
                ) : null,
            },
          ]}
        />
        {repaired && <p role="status" className="mt-3 text-sm text-green-700 dark:text-green-300">{repaired}</p>}
        {error !== undefined && !(canManage && !me?.viewAs) && <ErrorState error={error} lead="Couldn’t do that." />}
        {removed.length > 0 && (
          <p className="mt-3 text-xs text-neutral-500">
            {removed.length} removed in the last thirty days ({removed.map((u) => u.email ?? u.userId).join(", ")}); their sign-in is disabled and they are deleted after thirty days.
          </p>
        )}
        {!canManage && <p className="mt-3 text-sm text-neutral-500">Inviting, disabling and removing people is for managers of at least one environment.</p>}
      </div>
    </>
  );
}
